/** One portable ZIP: pinned direction + owned resources + actual preview witnesses. */
import { strToU8, zipSync } from "fflate";
import type { ReactNode } from "react";
import type { Manifest } from "./manifest";
import type { Patch } from "./patches";
import { frameOf, framePng, frameSize } from "./frames";

export interface Handoff {
  schema_version: 1;
  film_id: string;
  version: number;
  version_id: string;
  language: string;
  aspect: string;
  preview_patches?: Patch[];
  max_bytes: number;
  timeline_seconds: number;
  files: { path: string; data: string; sha256: string }[];
  resources: { path: string; scenes: number[]; role: string; rights: string }[];
  scenes: {
    id: string;
    number: number;
    name: string;
    seconds: number;
    film_start_s: number;
    prompt_path: string;
    prompt: string;
    captions: { text: string; from_s: number; to_s: number }[];
    camera: unknown;
    beats: unknown[];
    preview_moments: Record<"start" | "middle" | "end", number>;
  }[];
  requested_changes: {
    request_number: number;
    preview_verified: false;
    tasks: { text: string; scenes: string[] }[];
  } | null;
}

function safePath(path: string): boolean {
  return (
    /^[a-zA-Z0-9_./-]+$/.test(path) &&
    !path.startsWith("/") &&
    path.split("/").every((p) => p !== ".." && p !== "." && p !== "")
  );
}

async function sha(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new Uint8Array(data).buffer,
  );
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function handoffZip(
  handoff: Handoff,
  manifest: Manifest,
  options: {
    includeRequested: boolean;
    signal?: AbortSignal;
    wrap?: (node: ReactNode) => ReactNode;
    frame?: typeof frameOf;
    png?: typeof framePng;
  },
): Promise<{ zip: Blob; missing: string[] }> {
  if (
    handoff.schema_version !== 1 ||
    handoff.version !== manifest.film_version ||
    handoff.film_id !== manifest.film_id ||
    handoff.scenes.length !== manifest.scenes.length ||
    handoff.scenes.some((s, k) => s.id !== manifest.scenes[k].id) ||
    handoff.aspect !== manifest.aspect
  )
    throw new Error("Mismatched handoff version");
  options.signal?.throwIfAborted();
  const files: Record<string, Uint8Array> = {};
  const hashes: Record<string, string> = {};
  let size = 0;
  const add = async (path: string, bytes: Uint8Array) => {
    options.signal?.throwIfAborted();
    if (!safePath(path) || Object.hasOwn(files, path))
      throw new Error("Invalid or duplicate package path");
    size += bytes.byteLength;
    if (size > handoff.max_bytes) throw new Error("Package limit reached");
    files[path] = bytes;
    hashes[path] = await sha(bytes);
  };
  for (const f of handoff.files) {
    const bytes = Uint8Array.from(atob(f.data), (c) => c.charCodeAt(0));
    if ((await sha(bytes)) !== f.sha256)
      throw new Error("Resource integrity mismatch");
    await add(f.path, bytes);
  }
  const missing: string[] = [];
  const draw = options.frame ?? frameOf;
  const png = options.png ?? framePng;
  const previews: {
    path: string;
    scene: string;
    t: number;
    available: boolean;
  }[] = [];
  const requests = options.includeRequested ? handoff.requested_changes : null;
  const sceneFiles: string[] = [];
  for (const scene of handoff.scenes) {
    const tasks =
      requests?.tasks.filter((t) => t.scenes.includes(scene.id)) ?? [];
    // Intent is separate and higher priority than the old preview, never a claim of success.
    const prompt = tasks.length
      ? `${scene.prompt}\n\nREQUESTED DIRECTION — NOT VERIFIED IN THE PREVIEW\n` +
        "These requested changes override the existing scene description where they differ. " +
        "The preview shows the earlier version, not completion of these actions.\n" +
        tasks.map((t) => `- ${t.text}`).join("\n") +
        "\n"
      : scene.prompt;
    sceneFiles.push(scene.prompt_path);
    for (const [label, t] of Object.entries(scene.preview_moments)) {
      const path = `storyboard/${String(scene.number).padStart(2, "0")}-${label}.png`;
      let encoded: Uint8Array | null = null;
      let bitmap: ImageBitmap | null = null;
      try {
        bitmap = await draw(manifest, scene.id, t, frameSize(manifest.aspect), {
          wrap: options.wrap,
          captions: true,
          signal: options.signal,
        });
        encoded = await png(bitmap);
      } catch (error) {
        if (options.signal?.aborted) throw error;
        missing.push(path);
      } finally {
        bitmap?.close();
      }
      // A failed frame may be reported; an oversized package never becomes partial success.
      if (encoded) await add(path, encoded);
      previews.push({ path, scene: scene.id, t, available: encoded !== null });
    }
    const own = previews.filter((p) => p.scene === scene.id);
    const availability = own
      .map((p) => `${p.available ? "Available" : "MISSING"}: ${p.path}`)
      .join("\n");
    await add(
      scene.prompt_path,
      strToU8(
        prompt + "\nActual preview availability:\n" + availability + "\n",
      ),
    );
  }
  const scenario = {
    schema_version: 1,
    version: handoff.version,
    language: handoff.language,
    aspect: handoff.aspect,
    timeline_seconds: handoff.timeline_seconds,
    scenes: handoff.scenes.map(({ prompt: _prompt, ...s }) => s),
    resources: handoff.resources,
    requested_changes: requests,
    previews,
  };
  await add("scenario.json", strToU8(JSON.stringify(scenario, null, 2)));
  const director = files["DIRECTOR.txt"]
    ? new TextDecoder().decode(files["DIRECTOR.txt"]) + "\n\n"
    : "";
  const prompts =
    director +
    sceneFiles
      .map((p) => new TextDecoder().decode(files[p]))
      .join("\n\n---\n\n");
  await add("ALL-SCENES.txt", strToU8(prompts));
  await add(
    "PACKAGE.json",
    strToU8(
      JSON.stringify(
        {
          schema_version: 1,
          version: handoff.version,
          language: handoff.language,
          preview_role:
            "Existing TrueReel preview; requested changes are not visually verified",
          missing_previews: missing,
          sha256: hashes,
        },
        null,
        2,
      ),
    ),
  );
  const zipped = zipSync(files, { level: 0, mtime: new Date(1980, 0, 1) });
  options.signal?.throwIfAborted();
  if (zipped.byteLength > handoff.max_bytes)
    throw new Error("Package limit reached");
  return { zip: new Blob([zipped], { type: "application/zip" }), missing };
}
